/**
 * PaymentSettings — workspace default currency, preferred provider, and
 * supported currency picker. Surfaces live/sandbox status per provider so
 * creators understand which surface will actually take payments.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Coins, CheckCircle2, AlertCircle, Globe, Save, Loader2,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const PROVIDER_DESC = {
  paddle: { label: "Paddle", note: "Global merchant of record · handles VAT/tax · primary" },
  stripe: { label: "Stripe", note: "Cards · subscriptions · global" },
  razorpay: { label: "Razorpay", note: "India-first · UPI · INR" },
};

export default function PaymentSettings() {
  const [providers, setProviders] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c, s] = await Promise.all([
        api.get("/payments/providers"),
        api.get("/payments/currencies"),
        api.get("/payments/settings"),
      ]);
      setProviders(p.data || {});
      setCurrencies(c.data || []);
      setSettings(s.data || null);
    } catch {
      // soft fail
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const r = await api.patch("/payments/settings", {
        default_currency: settings.default_currency,
        preferred_provider: settings.preferred_provider || null,
        supported_currencies: settings.supported_currencies,
        tax_inclusive: settings.tax_inclusive,
        auto_localize: settings.auto_localize,
      });
      setSettings(r.data);
      toast.success("Payment settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const toggleCurrency = (code) => {
    if (!settings) return;
    const list = settings.supported_currencies || [];
    const next = list.includes(code) ? list.filter((c) => c !== code) : [...list, code];
    if (!next.length) return;       // require at least one
    setSettings({ ...settings, supported_currencies: next });
  };

  if (!settings) {
    return <div className="p-10 text-meta inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading payment settings…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="settings-payments">
      <div className="max-w-3xl space-y-6">

        {/* Provider status */}
        <div className="surface p-6" data-testid="payments-providers-card">
          <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2 mb-1">
            <Coins className="h-4 w-4 text-ai" /> Payment providers
          </h3>
          <p className="text-sm text-meta mb-4">
            International-first. Paddle handles tax/VAT globally as the primary provider. Stripe + Razorpay come online when their env keys are set.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.values(providers).map((p) => (
              <div key={p.name} className="rounded-xl border border-line bg-white p-3" data-testid={`payments-provider-${p.name}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-ink capitalize">{PROVIDER_DESC[p.name]?.label || p.name}</p>
                  {p.live ? (
                    <span className="pill pill-success inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Live</span>
                  ) : (
                    <span className="pill pill-warning inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Sandbox</span>
                  )}
                </div>
                <p className="text-[11px] text-meta">{PROVIDER_DESC[p.name]?.note}</p>
                <p className="text-[10px] text-meta-soft mt-1.5">{p.currencies?.length} currencies</p>
              </div>
            ))}
          </div>
        </div>

        {/* Default currency */}
        <div className="surface p-6" data-testid="payments-default-currency">
          <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2 mb-1">
            <Globe className="h-4 w-4 text-ai" /> Default currency
          </h3>
          <p className="text-sm text-meta mb-4">
            Buyers see this currency on checkout when an asset doesn't override it.
          </p>
          <select
            value={settings.default_currency}
            onChange={(e) => setSettings({ ...settings, default_currency: e.target.value })}
            data-testid="payments-default-currency-select"
            className="w-full sm:w-72 rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.symbol} {c.code} — {c.label}{c.primary ? " (recommended)" : ""}
              </option>
            ))}
          </select>
          <div className="mt-3 rounded-xl bg-zinc-50 border border-line px-3 py-2 text-xs text-ink-soft">
            Resolved provider: <span className="font-semibold text-ink">{settings.resolved_provider?.name}</span>
            <span className="text-meta"> · reason: {settings.resolved_provider?.reason}</span>
            {settings.resolved_provider?.sandbox && <span className="ml-2 pill pill-warning text-[10px]">sandbox</span>}
          </div>
        </div>

        {/* Preferred provider */}
        <div className="surface p-6" data-testid="payments-preferred-provider">
          <h3 className="font-display text-lg font-bold text-ink mb-1">Preferred provider (optional)</h3>
          <p className="text-sm text-meta mb-3">
            Override the default routing. Leave on "Auto" to let Ashra pick based on currency + region.
          </p>
          <div className="inline-flex rounded-full bg-white border border-line p-1" data-testid="payments-preferred-switch">
            {[
              { id: "", label: "Auto" },
              { id: "paddle", label: "Paddle" },
              { id: "stripe", label: "Stripe" },
              { id: "razorpay", label: "Razorpay" },
            ].map((opt) => (
              <button
                key={opt.id || "auto"}
                onClick={() => setSettings({ ...settings, preferred_provider: opt.id || null })}
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
                  (settings.preferred_provider || "") === opt.id
                    ? "ashra-gradient-bg text-white shadow-sm"
                    : "text-meta hover:text-ink"
                }`}
                data-testid={`payments-preferred-${opt.id || "auto"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Supported currencies (per-asset override pool) */}
        <div className="surface p-6" data-testid="payments-supported-currencies">
          <h3 className="font-display text-lg font-bold text-ink mb-1">Supported currencies</h3>
          <p className="text-sm text-meta mb-3">
            Choose which currencies your team can assign to individual assets. Keep this lean for clean UX.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {currencies.map((c) => {
              const on = (settings.supported_currencies || []).includes(c.code);
              return (
                <button
                  key={c.code}
                  onClick={() => toggleCurrency(c.code)}
                  data-testid={`payments-currency-toggle-${c.code}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition border ${
                    on ? "ashra-gradient-bg text-white border-transparent shadow-sm" : "bg-white border-line text-ink-soft hover:border-ai hover:text-ai"
                  }`}
                >
                  {c.symbol} {c.code}
                </button>
              );
            })}
          </div>
        </div>

        {/* Behavioural flags */}
        <div className="surface p-6 space-y-3" data-testid="payments-flags">
          <h3 className="font-display text-lg font-bold text-ink">Checkout behaviour</h3>
          <label className="flex items-start gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={!!settings.tax_inclusive}
              onChange={(e) => setSettings({ ...settings, tax_inclusive: e.target.checked })}
              className="accent-violet-600 mt-0.5"
              data-testid="payments-tax-inclusive-toggle"
            />
            <span>
              <span className="font-semibold text-ink">Tax-inclusive pricing</span>
              <span className="block text-xs text-meta">Display gross prices. Paddle calculates VAT/sales tax automatically.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={!!settings.auto_localize}
              onChange={(e) => setSettings({ ...settings, auto_localize: e.target.checked })}
              className="accent-violet-600 mt-0.5"
              data-testid="payments-auto-localize-toggle"
            />
            <span>
              <span className="font-semibold text-ink">Auto-localize currency</span>
              <span className="block text-xs text-meta">Detect buyer's region and present a supported local currency at checkout.</span>
            </span>
          </label>
        </div>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={busy}
            data-testid="payments-save-btn"
            className="rounded-full ashra-gradient-bg px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save settings
          </button>
        </div>
      </div>
    </div>
  );
}
